import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Item } from '../engine/types';

/** Custom templates of a team or an organization (built-in ones live in engine/templates.ts). */
export const CATEGORIES = ['retro', 'planning', 'research', 'strategy', 'education', 'other'] as const;
export type TemplateCategory = (typeof CATEGORIES)[number];

export type TemplateScope = { scope: 'team'; teamId: string } | { scope: 'org'; orgId: string };

export interface CustomTemplate {
  id: string;
  scope: 'team' | 'org';
  teamId: string | null;
  teamName: string | null;
  /** Saved to the creator's personal space: only they see it. */
  personal: boolean;
  orgId: string | null;
  orgName: string | null;
  name: string;
  description: string;
  category: TemplateCategory;
  itemCount: number;
  preview: string | null;
  createdBy: { id: string; name: string | null } | null;
  createdAt: number;
  updatedAt: number;
  canEdit: boolean;
}

export interface CustomTemplateWithItems extends CustomTemplate {
  items: Item[];
}

/** Same limits as the server (crates/server/src/api/templates.rs). */
export const MAX_ITEMS = 5000;
export const MAX_ITEMS_BYTES = 2 << 20;

export const templatesKey = ['templates'] as const;

export function useCustomTemplates(enabled = true) {
  return useQuery({ queryKey: templatesKey, queryFn: () => api.get<CustomTemplate[]>('/templates'), enabled, staleTime: 30_000 });
}

export const templatesApi = {
  get: (id: string) => api.get<CustomTemplateWithItems>(`/templates/${id}`),
  create: (body: TemplateScope & { name: string; description: string; category: TemplateCategory; items: Item[] }) => api.post<CustomTemplate>('/templates', body),
  update: (id: string, body: Partial<Pick<CustomTemplate, 'name' | 'description' | 'category'>>) => api.patch<CustomTemplate>(`/templates/${id}`, body),
  remove: (id: string) => api.del<{ ok: true }>(`/templates/${id}`),
  putPreview: (id: string, image: Blob) => api.upload<{ ok: true }>(`/templates/${id}/preview`, image, 'PUT'),
};

/** `?template=` value for a board created from a custom template; built-in ones use their bare id. */
const PARAM_PREFIX = 'team:';
export const customTemplateParam = (id: string) => `${PARAM_PREFIX}${id}`;
export const parseCustomTemplateParam = (param: string | null): string | null =>
  param?.startsWith(PARAM_PREFIX) ? param.slice(PARAM_PREFIX.length) || null : null;

/** Case-insensitive match on name and description, plus the category filter ('all' = any). */
export function filterTemplates(list: CustomTemplate[], category: TemplateCategory | 'all', query: string): CustomTemplate[] {
  const q = query.trim().toLocaleLowerCase();
  return list.filter(
    (tpl) => (category === 'all' || tpl.category === category) && (!q || tpl.name.toLocaleLowerCase().includes(q) || tpl.description.toLocaleLowerCase().includes(q)),
  );
}
